import { CheckIcon } from '@heroicons/react/24/solid';
import Button from 'src/components/button/button';
import {
  useImportConfiguration,
  IMPORT_CONFIGURATION_STEPS,
} from 'src/context/import-configuration';
import { twMerge } from 'tailwind-merge';

import { DialogWrapper } from '../dialog/shared/dialog-wrapper';
import ImportConfigurationStepFiles from './steps/import-configuration-step-files';
import ImportStepConnectionsReview from './steps/import-step-connections-review';
import ImportStepGlobalSettings from './steps/import-step-global-settings';
import ImportStepNodesReview from './steps/import-step-nodes-review';
import ImportStepSummary from './steps/import-step-summary';
import ImportStepVlanSubnet from './steps/import-step-vlan-subnet';
import { ImportWizardStepShell } from './wizard/import-wizard-step-shell';

export default function ImportConfigurationDialog() {
  const {
    currentStep,
    dialogState,
    confirmImport,
    closeDialog,
    handlePrev,
    handleNext,
    canProceedFromCurrentStep,
  } = useImportConfiguration();

  if (!dialogState) return null;

  return (
    <DialogWrapper
      title="Import Configuration"
      onClose={closeDialog}
      testId="import-configuration-dialog"
      panelClassName="max-w-[96%]"
      disableClickOutside
      footer={
        <div className="flex justify-between gap-3">
          {currentStep > 1 ? (
            <Button type="button" color="gray" onClick={handlePrev}>
              Previous
            </Button>
          ) : (
            <div />
          )}
          <Button
            type="button"
            color="blue"
            onClick={
              currentStep === IMPORT_CONFIGURATION_STEPS.length
                ? confirmImport
                : handleNext
            }
            disabled={
              currentStep === IMPORT_CONFIGURATION_STEPS.length
                ? !dialogState.projectJsonData
                : !canProceedFromCurrentStep
            }
          >
            {currentStep === IMPORT_CONFIGURATION_STEPS.length
              ? 'Import'
              : 'Next'}
          </Button>
        </div>
      }
    >
      <div className="flex items-center justify-center px-2">
        <div className="w-full">
          <div className="relative mb-4 flex items-start justify-between">
            <div className="absolute left-0 right-0 top-5 z-0 h-px bg-gray-300" />
            {IMPORT_CONFIGURATION_STEPS.map((step) => {
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              return (
                <div
                  key={step.id}
                  className="relative z-10 flex flex-1 flex-col items-center"
                >
                  <div
                    className={twMerge([
                      'flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300',
                      isActive || isCompleted
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-500',
                    ])}
                  >
                    {isCompleted ? (
                      <CheckIcon className="h-6 w-6" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  <div className="mt-1 text-center">
                    <p
                      className={twMerge([
                        'whitespace-nowrap text-sm font-bold',
                        isActive || isCompleted
                          ? 'text-gray-900'
                          : 'text-gray-400',
                      ])}
                    >
                      {step.label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Step Content */}
          <ImportWizardStepShell step={currentStep}>
            <div className="mb-2 flex min-h-32 items-start justify-center rounded border border-gray-100 bg-gray-50 p-4 md:p-6">
              {currentStep === 1 && <ImportConfigurationStepFiles />}
              {currentStep === 2 && <ImportStepVlanSubnet />}
              {currentStep === 3 && <ImportStepNodesReview mode="switch" />}
              {currentStep === 4 && <ImportStepNodesReview mode="device" />}
              {currentStep === 5 && <ImportStepConnectionsReview />}
              {currentStep === 6 && <ImportStepGlobalSettings />}
              {currentStep === 7 && <ImportStepSummary />}
            </div>
          </ImportWizardStepShell>
        </div>
      </div>
    </DialogWrapper>
  );
}
